import React, { useState, useEffect } from 'react';
import { profilePictureAPI } from '../../services/api';

const AdminProfilePicture = () => {
  const [profilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    loadProfilePicture();
  }, []);

  const loadProfilePicture = async () => {
    try {
      const response = await profilePictureAPI.get();
      if (response.data.file_path) {
        setProfilePicture(response.data);
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        setPreview(`${baseUrl}${response.data.file_path}`);
      }
    } catch (error) {
      console.error('Error loading profile picture:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setMessage('Please select a valid image file (jpeg, jpg, png, gif, webp)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('File size should be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('profilePictureInput');
    const file = fileInput.files[0];

    if (!file) {
      setMessage('Please select a file');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await profilePictureAPI.upload(file);
      setMessage('Profile picture uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
      loadProfilePicture();
      fileInput.value = '';
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error uploading profile picture');
      console.error('Error uploading profile picture:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the profile picture?')) return;

    setLoading(true);
    setMessage('');

    try {
      await profilePictureAPI.delete();
      setProfilePicture(null);
      setPreview(null);
      setMessage('Profile picture deleted successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error deleting profile picture');
      console.error('Error deleting profile picture:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-gray-800 dark:text-gray-200 text-2xl font-bold mb-4">Profile Picture</h2>
      {message && (
        <div style={{ 
          color: message.includes('success') ? 'green' : 'red', 
          marginBottom: '1rem',
          padding: '0.75rem',
          background: message.includes('success') ? '#d4edda' : '#f8d7da',
          borderRadius: '8px',
          border: `1px solid ${message.includes('success') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      {preview && (
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <img 
            src={preview} 
            alt="Profile Preview" 
            style={{
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: '5px solid #667eea',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
            }}
          />
        </div>
      )}

      <form onSubmit={handleUpload}>
        <div className="form-group">
          <label>Upload Profile Picture</label>
          <input
            id="profilePictureInput"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            required={!profilePicture}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
            Supported formats: JPEG, JPG, PNG, GIF, WEBP (Max size: 5MB)
          </small>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Uploading...' : profilePicture ? 'Update Picture' : 'Upload Picture'}
          </button>
          {profilePicture && (
            <button 
              type="button" 
              className="btn btn-danger" 
              onClick={handleDelete}
              disabled={loading}
            >
              Delete Picture
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AdminProfilePicture;

